import React, { ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { motion, HTMLMotionProps } from 'motion/react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'emerald';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  isLoading?: boolean;
}

const buttonVariants = {
  primary: 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-sm border border-emerald-500/50',
  secondary: 'bg-white hover:bg-slate-50 text-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 shadow-sm',
  outline: 'bg-transparent hover:bg-slate-100 text-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700',
  ghost: 'bg-transparent hover:bg-slate-100 text-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 border fill-transparent border-transparent',
  danger: 'bg-red-600 hover:bg-red-700 text-white shadow-sm border border-transparent',
  emerald: 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm border border-transparent',
};

const iconVariants = {
  primary: 'text-white/70 hover:text-white',
  secondary: 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200',
  outline: 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200',
  ghost: 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200',
  danger: 'text-white/70 hover:text-white',
  emerald: 'text-white/70 hover:text-white',
};

const buttonSizes = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
  icon: 'h-9 w-9 flex items-center justify-center p-0',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', isLoading, disabled, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:pointer-events-none disabled:opacity-50 rounded-lg cursor-pointer',
          buttonVariants[variant],
          buttonSizes[size],
          className
        )}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : null}
        {children}
      </button>
    );
  }
);
Button.displayName = 'Button';
